/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  PLATFORM_ID,
  Signal,
  viewChild,
  ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { map } from 'rxjs';

import { BrandMovieService } from '@usheru-hff/movie-detail/feature-brand-movie-conversion-vs-awareness';
import { GsapAnimationService } from '@usheru-hff/core/gsap';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MovieTellFriendsComponent } from '@usheru-hff/movie-detail/ui-movie-elements';
import { MovieSiteImageConfig } from '@usheru-hff/sdk/movie-site';
import { getChannelMovieProp, Movie } from '@usheru-hff/shared/domain';
import { VideoFullscreenService } from '@usheru-hff/shared/ui-video';

import { MovieSiteSideMenuComponent } from './movie-site-side-menu.component';
import { MovieSiteLogoImageComponent } from '../logo-image/movie-site-logo-image.component';
import { MovieSiteConfigurationService } from '../movie-site-configuration.service';
import { MovieSiteNavActionComponent } from '../nav-action/movie-site-nav-action.component';
import { NavAction } from '../nav-action/movie-site-nav-action.model';
import { MovieSiteSideMenuColorSchemeConfiguration } from './movie-site-side-menu-color-scheme.model';
import { MovieSiteNavActionService } from '../nav-action/movie-site-nav-action.service';

const INITIAL_IMAGES: MovieSiteImageConfig[] = [
  {
    type: 'channel-logo',
    background: 'unset',
    padding: '0.24rem',
    fit: 'contain'
  }
];

const INITIAL_BACKGROUND = 'transparent';
const INITIAL_CONTROLS_BACKGROUND = 'var(--brand-color)';
const INITIAL_CONTROLS_COLOR = 'var(--brand-color-contrast)';
const MOVIE_SITE_PAGES_WITH_SCROLL_PROGRESS = new Set(['film-info', 'book-now', 'watch-now']);

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MovieSiteLogoImageComponent,
    MovieSiteNavActionComponent,
    MovieSiteSideMenuComponent,
    MovieTellFriendsComponent
  ],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'movie-site_top-menu',
  templateUrl: './movie-site-top-menu.component.html',
  styleUrls: ['./movie-site-top-menu.component.scss'],
  // The sidemenu is defined here but is append outside of this component
  // so we need to use ViewEncapsulation.None to style that part of the component
  // from this component style file
  encapsulation: ViewEncapsulation.None
})
export class MovieSiteTopMenuComponent {
  readonly route = inject(ActivatedRoute);
  readonly brandMovieService = inject(BrandMovieService);
  readonly channelService = inject(ChannelService);
  readonly configService = inject(MovieSiteConfigurationService);
  readonly videoService = inject(VideoFullscreenService);
  readonly platformId = inject(PLATFORM_ID);
  private readonly gsapAnimationService = inject(GsapAnimationService);
  private readonly navActionService = inject(MovieSiteNavActionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  @HostBinding('class.movie-site-top-menu') className = true;
  @HostBinding('style.--top-menu-background-large') backgroundLarge = INITIAL_BACKGROUND;
  @HostBinding('style.--top-menu-background-small')
  get topMenuBackgroundSmall() {
    const config = this.topMenuConfiguration();
    return config.backgroundSmallScreen ?? 'var(--background-color)';
  }

  @HostBinding('style.--movie-site-top-menu-burger-background')
  get burgerBackground() {
    return this.topMenuConfiguration()?.burgerBackground ?? null;
  }

  @HostBinding('style.--movie-site-top-menu-burger-color')
  get burgerColor() {
    const contrast = this.burgerContrast();
    if (contrast === 'white') {
      return '#ffffff';
    }
    if (contrast === 'black') {
      return '#000000';
    }
    return null;
  }

  @HostBinding('style.--sidemenu-control-background') controlBackground = INITIAL_CONTROLS_BACKGROUND;
  @HostBinding('style.--sidemenu-control-color') controlColor = INITIAL_CONTROLS_COLOR;
  @HostListener('window:resize')
  resizeHandler() {
    this.closeSideMenuOnBreakpoint();
  }

  readonly currentPage = toSignal(
    this.route.data.pipe(
      takeUntilDestroyed(),
      map(data => data['page'] as string | undefined)
    ),
    {
      initialValue: this.route.snapshot.data['page'] as string | undefined
    }
  );

  readonly sidemenu: Signal<MovieSiteSideMenuComponent | undefined> = viewChild(MovieSiteSideMenuComponent);
  readonly headerScrollProgressFill: Signal<ElementRef<HTMLElement> | undefined> =
    viewChild('headerScrollProgressFill');

  readonly topMenuConfiguration = this.configService.topMenuConfiguration;

  navActions = input.required<NavAction[]>();
  configuredNavActions = computed(() =>
    this.navActionService.insertCustomNavigationButton(
      this.navActions(),
      this.configService.customNavigationButtonConfiguration()
    )
  );
  movie = input<Movie | undefined>(undefined);

  readonly sideMenuColorScheme = input<MovieSiteSideMenuColorSchemeConfiguration>();

  appendSidemenuTo = input<HTMLElement | undefined>(undefined);
  private headerScrollProgressElement?: HTMLElement;
  private headerScrollProgressCleanup?: () => void;

  images = computed(
    () =>
      this.configService.topMenuConfiguration()?.logos ||
      this.configService.movieSiteConfigGlobal()?.topMenu?.logos ||
      INITIAL_IMAGES
  );

  topMenuSmallContrast = computed(() => this.configService.contrastMap()?.topMenuSmall);
  topMenuLargeContrast = computed(() => this.configService.contrastMap()?.topMenuLarge);
  burgerContrast = computed(() => this.configService.contrastMap()?.burger);
  readonly customSideMenuColorScheme = computed(() => {
    // The Input one
    const sideMenuColorScheme = this.sideMenuColorScheme();
    if (sideMenuColorScheme) {
      return sideMenuColorScheme;
    }
    // The one from the configuration
    const sideMenuColorSchemeFromConfig = this.configService.sideMenuColorScheme();

    if (sideMenuColorSchemeFromConfig) {
      return sideMenuColorSchemeFromConfig;
    }
  });
  sideMenuContrast = computed(() => this.configService.contrastMap()?.sideMenu);
  readonly sideMenuLogoContrast = computed(
    () => this.customSideMenuColorScheme()?.logoContrast ?? this.sideMenuContrast()
  );

  logosHidden = computed(() => this.movie()?.properties?.movieSite?.topMenu?.hideLogos ?? false);
  sideMenuLogosFilter = computed(() => {
    const mode = this.movie()?.properties?.topMenu?.sideMenu?.logosColorMode;
    if (mode === 'force-white') {
      return 'brightness(0) invert(1)';
    }
    if (mode === 'invert') {
      return 'brightness(1) invert(1)';
    }
    return null;
  });
  headerScrollProgressEnabled = computed(() => {
    const movie = this.movie();
    const currentPage = this.currentPage();
    if (!movie || !currentPage || !MOVIE_SITE_PAGES_WITH_SCROLL_PROGRESS.has(currentPage)) {
      return false;
    }

    return getChannelMovieProp('movieSite.gsap.headerScrollProgress', this.channelService.channel, movie) ?? false;
  });

  readonly routePath = '.';
  readonly TrackingCategory = 'MovieDetail';
  readonly faPlay = faPlay;

  constructor() {
    effect(() => {
      const topMenuConfiguration = this.configService.topMenuConfiguration();
      const globalTopMenuConfig = this.globalTopMenuConfig;
      this.backgroundLarge =
        topMenuConfiguration?.backgroundLargeScreen || globalTopMenuConfig?.backgroundLargeScreen || INITIAL_BACKGROUND;
      this.controlBackground = topMenuConfiguration.controls?.background || INITIAL_CONTROLS_BACKGROUND;
      this.controlColor = topMenuConfiguration.controls?.color || INITIAL_CONTROLS_COLOR;
    });

    effect(() => {
      const progressFill = this.headerScrollProgressFill()?.nativeElement;
      if (!this.headerScrollProgressEnabled() || !progressFill || progressFill === this.headerScrollProgressElement) {
        return;
      }

      // The fill element was recreated (e.g. navigating between pages) - rebind to the new one.
      this.headerScrollProgressCleanup?.();
      this.headerScrollProgressCleanup = undefined;
      this.headerScrollProgressElement = progressFill;
      void this.initHeaderScrollProgress(progressFill);
    });

    effect(() => {
      this.gsapAnimationService.syncMovieSiteGsapConfigAttributes(this.channelService.channel, this.movie());
    });

    this.destroyRef.onDestroy(() => this.gsapAnimationService.clearMovieSiteGsapConfigAttributes());
  }

  private async initHeaderScrollProgress(progress: HTMLElement): Promise<void> {
    const cleanup = await this.gsapAnimationService.initScrollProgress(
      progress,
      this.elementRef.nativeElement,
      this.destroyRef
    );

    // A newer fill element was initialized while awaiting - tear this stale one down.
    if (this.headerScrollProgressElement !== progress) {
      cleanup();
      return;
    }

    this.headerScrollProgressCleanup = cleanup;
  }

  closeSideMenuOnBreakpoint() {
    const sidemenu = this.sidemenu();
    // Check if pass breakpoint of 992px and close sidemenu if open
    if (this.innerWidth >= 992 && sidemenu) {
      sidemenu.close();
    }
  }

  getCustomChannelLogoHeight(image: MovieSiteImageConfig, topMenu: 'large' | 'small' | 'sidemenu'): string | null {
    if (image.type !== 'channel-logo') return null;
    const topMenuConfig = this.topMenuConfiguration();
    switch (topMenu) {
      case 'small':
        return topMenuConfig?.mainLogoHeightOverMenu?.smallScreen || null;
      case 'large':
        return topMenuConfig?.mainLogoHeightOverMenu?.largeScreen || null;
      case 'sidemenu':
        return topMenuConfig?.mainLogoHeightOverMenu?.sidemenu || null;
    }
    return null;
  }

  get innerWidth(): number | null {
    if (isPlatformBrowser(this.platformId)) {
      return window.innerWidth;
    }
    return null;
  }

  get globalTopMenuConfig() {
    return this.configService.movieSiteConfigGlobal()?.topMenu;
  }

  onClinkNavAction() {
    // Close the sidemenu when a nav action is clicked
    const sidemenu = this.sidemenu();
    if (!sidemenu) return;
    sidemenu.close();
  }
}
